/**
 * ASCII Art Generator - Dither2 渲染引擎
 *
 * 使用不同的抖动算法(默认Atkinson)产生不同的点阵效果，支持动画渲染
 */

import type {
  IRenderEngine,
  FullRenderSettings,
  RenderResult,
  StyleType,
  RGB,
} from '../types'
import type { AnimationContext } from '../animation/types'
import { applyColorMode } from '../algorithms/grayscale'
import { imageDataToGrayscaleArray, atkinsonDither } from '../algorithms/dithering'
import { getCharByBrightness, CHARSET_STANDARD } from '../charsets'
import { calculateCharacterAnimationOffset, createStaticContext } from './animationHelpers'

export class Dither2Engine implements IRenderEngine {
  readonly name: StyleType = 'dither2'

  /**
   * 渲染图片为 Dither2 艺术（静态或动画）
   *
   * @param source 源图像
   * @param settings 渲染设置
   * @param canvas 目标 canvas
   * @param context 可选的动画上下文，不提供则为静态渲染
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext = context ?? createStaticContext()

    const imageData = this.getImageData(source)
    const { width: imgWidth, height: imgHeight } = imageData

    const { fontSize, charSpacing, invert, dither2ColorNum, contrast, brightness } = settings
    const charWidth = fontSize * 0.6 * charSpacing
    const charHeight = fontSize

    const cols = Math.max(1, Math.floor(imgWidth / charWidth))
    const rows = Math.max(1, Math.floor(imgHeight / charHeight))

    canvas.width = cols * charWidth
    canvas.height = rows * charHeight

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.font = `${fontSize}px ${settings.renderFont}`
    ctx.textBaseline = 'top'

    // 转换为灰度数组并应用对比度、亮度，然后使用Atkinson抖动
    const grayArray = imageDataToGrayscaleArray(imageData, contrast, brightness)
    const dithered = atkinsonDither(grayArray, imgWidth, imgHeight, dither2ColorNum || 4)

    const sampleWidth = imgWidth / cols
    const sampleHeight = imgHeight / rows

    // 检查是否启用动画
    const useAnimation = settings.dither2AnimationEnabled

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const sampleX = Math.floor(col * sampleWidth + sampleWidth / 2)
        const sampleY = Math.floor(row * sampleHeight + sampleHeight / 2)

        if (sampleX < imgWidth && sampleY < imgHeight) {
          const idx = sampleY * imgWidth + sampleX
          let finalBrightness = dithered[idx]
          if (invert) finalBrightness = 255 - finalBrightness

          const char = getCharByBrightness(finalBrightness, CHARSET_STANDARD)
          const color = applyColorMode(
            { r: finalBrightness, g: finalBrightness, b: finalBrightness },
            settings.colorMode,
            settings.customColor
          )

          // 计算字符基础位置
          const baseX = col * charWidth
          const baseY = row * charHeight

          // 应用动画偏移（如果启用）
          let drawX = baseX
          let drawY = baseY

          if (useAnimation) {
            const offset = calculateCharacterAnimationOffset(
              baseX, baseY, animContext, settings, charWidth, charHeight
            )
            drawX += offset.offsetX
            drawY += offset.offsetY
          }

          ctx.fillStyle = this.applyOpacity(color, settings.opacity)
          ctx.fillText(char, drawX, drawY)
        }
      }
    }

    return {
      canvas,
      width: canvas.width,
      height: canvas.height,
      charWidth,
      charHeight
    }
  }

  getOutputSize(
    imageWidth: number,
    imageHeight: number,
    settings: FullRenderSettings
  ): { width: number; height: number } {
    const { fontSize, charSpacing } = settings
    const charWidth = fontSize * 0.6 * charSpacing
    const charHeight = fontSize
    return {
      width: Math.floor(imageWidth / charWidth) * charWidth,
      height: Math.floor(imageHeight / charHeight) * charHeight
    }
  }

  private getImageData(
    source: HTMLImageElement | HTMLCanvasElement | ImageData
  ): ImageData {
    if (source instanceof ImageData) return source
    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }

  private applyOpacity(color: RGB, opacity: number): string {
    return opacity < 1
      ? `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`
      : `rgb(${color.r}, ${color.g}, ${color.b})`
  }
}

export const dither2Engine = new Dither2Engine()